import React, { useState, useMemo } from 'react'
import { TableVirtuoso } from 'react-virtuoso';

// icons
import { MdFlight, MdLocationOn } from "react-icons/md";

// tooltip for long cell values (e.g. Comments)
import { CustomTooltip } from '_organisms';

//#region styles
const containerStyle = {
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse', // important
    tableLayout: 'fixed', // prevents cell content from stretching column widths
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    fontSize: '14px',
    fontFamily: 'system-ui, -apple-system, sans-serif'
};

const headerStyle = {
    backgroundColor: '#f8fafc',
    color: '#374151',
    fontWeight: '600',
    padding: '12px 16px',
    textAlign: 'left',
    borderBottom: '2px solid #e5e7eb',
    borderRight: '1px solid #e5e7eb',
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
};

const cellStyle = {
    padding: '12px 16px',
    borderBottom: '1px solid #f3f4f6',
    borderTop: '1px solid #f3f4f6',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

const flightExpandedColumnStyle = {
    backgroundColor: '#eff6ff'
};

const emptyStateStyle = {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#6b7280',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    height: '100%'
};

const CRT_STYLE = `
.crt-row:hover > td { background-color: #f9fafb; }
`;

// nested table styles
const nestedTableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    fontSize: '12px',
    margin: '8px 0',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    tableLayout: 'fixed'
};

// flight info table styles
const flightTableStyle = {
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    fontSize: '12px',
    margin: '8px 0',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    tableLayout: 'auto'
};

const flightHeaderStyle = {
    backgroundColor: '#f1f5f9',
    color: '#475569',
    fontWeight: '500',
    padding: '8px 12px',
    textAlign: 'left',
    borderBottom: '1px solid #e5e7eb',
    borderRight: '1px solid #e5e7eb',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    whiteSpace: 'nowrap',
};

const flightCellStyle = {
    padding: '8px 12px',
    borderBottom: '1px solid #f3f4f6',
    borderRight: '1px solid #f3f4f6',
    color: '#374151',
    fontSize: '11px',
    whiteSpace: 'nowrap',
};

const flightTitleStyle = {
    fontSize: '12px',
    fontWeight: 600,
    color: '#475569',
    margin: '0 0 4px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
};
//#endregion

// Flight info table component — renders airport-schedule flight doc fields for a passenger's flight
const FlightInfoTable = ({ flightInfo }) => {
    if (!flightInfo) {
        return (
            <div style={{ padding: '8px 12px', fontSize: '12px', color: '#6b7280' }}>
                No flight information available for this passenger.
            </div>
        );
    }

    const formatMaybeTime = (val) => {
        if (val === null || val === undefined || val === '') return 'N/A';
        if (typeof val === 'string' && /\d{4}-\d{2}-\d{2}/.test(val)) {
            const d = new Date(val);
            if (!isNaN(d.getTime())) {
                return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
            }
        }
        return String(val);
    };

    const op = flightInfo.operationInfo || {};
    const cols = [
        ['Flight Doc ID', flightInfo.id],
        ['Gate', flightInfo.gate],
        ['Stand', flightInfo.stand],
        ['Terminal', flightInfo.terminal],
        ['Aircraft Type', flightInfo.aircraftType],
        ['Aircraft Reg', flightInfo.aircraftRegistration],
        ['Seat Capacity', flightInfo.seatCapacity],
        ['Boarding Time', formatMaybeTime(flightInfo.boardingTime)],
        ['Scheduled Time', formatMaybeTime(flightInfo.originalFlightTime)],
        ['Op Status', op.status ?? op.statusType ?? flightInfo.flightStatus],
        ['Op Time', op.time ? formatMaybeTime(op.time) : null],
        ['Call Sign', flightInfo.callSign],
    ];

    const display = (v) => (v === null || v === undefined || v === '' ? 'N/A' : String(v));

    // Operating times (Dublin only) — render every timestamp present
    const operatingTimes = flightInfo.operatingTimes || null;
    const operatingTimesCols = operatingTimes
        ? Object.entries(operatingTimes).map(([key, value]) => [
            key.replace(/DateTime$/, '').replace(/([A-Z])/g, ' $1').trim(),
            formatMaybeTime(value),
        ])
        : [];

    // Linked flight (Dublin only)
    const linkedFlight = flightInfo.linkedFlight || null;
    const linkedFlightCols = linkedFlight
        ? [
            ['Flight', linkedFlight.FlightIdentity ?? `${linkedFlight.IATAFlightIdentifier?.CarrierIATACode ?? ''}${linkedFlight.IATAFlightIdentifier?.FlightNumber ?? ''}`],
            ['Scheduled Date', linkedFlight.ScheduledDateUTC],
            ['Flight Key', linkedFlight.FlightKey],
        ]
        : [];

    // Flight notes — may be an array of note objects/strings or a plain string
    const rawNotes = flightInfo.flightNotes;
    let notesText = '';
    if (Array.isArray(rawNotes)) {
        notesText = rawNotes
            .map((n) => (typeof n === 'string' ? n : (n?.text ?? n?.note ?? '')))
            .filter(Boolean)
            .join('; ');
    } else if (typeof rawNotes === 'string') {
        notesText = rawNotes;
    }

    // History rows
    const history = Array.isArray(flightInfo.history) ? flightInfo.history : [];
    const historyChanged = (h) => {
        const changes = [];
        if (h?.gateChanged) changes.push('Gate');
        if (h?.standChanged) changes.push('Stand');
        if (h?.terminalChanged) changes.push('Terminal');
        if (h?.operationTimeChanged) changes.push('Op Time');
        return changes.length ? changes.join(', ') : '-';
    };

    return (
        <div>
            <p style={flightTitleStyle}>
                <MdFlight size={14} color={'#2563eb'} /> Flight Information
            </p>
            <table style={flightTableStyle}>
                <thead>
                    <tr>
                        {cols.map(([label], i) => (
                            <th key={i} style={flightHeaderStyle}>{label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        {cols.map(([, value], i) => (
                            <td key={i} style={flightCellStyle}>{display(value)}</td>
                        ))}
                    </tr>
                </tbody>
            </table>

            {operatingTimesCols.length > 0 && (
                <>
                    <p style={flightTitleStyle}>Operating Times</p>
                    <table style={flightTableStyle}>
                        <thead>
                            <tr>
                                {operatingTimesCols.map(([label], i) => (
                                    <th key={i} style={flightHeaderStyle}>{label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                {operatingTimesCols.map(([, value], i) => (
                                    <td key={i} style={flightCellStyle}>{display(value)}</td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </>
            )}

            {linkedFlightCols.length > 0 && (
                <>
                    <p style={flightTitleStyle}>Linked Flight</p>
                    <table style={flightTableStyle}>
                        <thead>
                            <tr>
                                {linkedFlightCols.map(([label], i) => (
                                    <th key={i} style={flightHeaderStyle}>{label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                {linkedFlightCols.map(([, value], i) => (
                                    <td key={i} style={flightCellStyle}>{display(value)}</td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </>
            )}

            {notesText && (
                <>
                    <p style={flightTitleStyle}>Flight Notes</p>
                    <div style={{ padding: '8px 12px', fontSize: '11px', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '4px', backgroundColor: 'white' }}>
                        {notesText}
                    </div>
                </>
            )}

            {history.length > 0 && (
                <>
                    <p style={flightTitleStyle}>Update History</p>
                    <table style={flightTableStyle}>
                        <thead>
                            <tr>
                                {['Updated At', 'Gate', 'Stand', 'Terminal', 'Op Status', 'Op Time', 'Changed'].map((label, i) => (
                                    <th key={i} style={flightHeaderStyle}>{label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((h, i) => (
                                <tr key={i}>
                                    <td style={flightCellStyle}>{formatMaybeTime(h?.updatedAt)}</td>
                                    <td style={flightCellStyle}>{display(h?.gate)}</td>
                                    <td style={flightCellStyle}>{display(h?.stand)}</td>
                                    <td style={flightCellStyle}>{display(h?.terminal)}</td>
                                    <td style={flightCellStyle}>{display(h?.operationInfo?.status ?? h?.operationInfo?.statusType)}</td>
                                    <td style={flightCellStyle}>{h?.operationInfo?.time ? formatMaybeTime(h.operationInfo.time) : 'N/A'}</td>
                                    <td style={flightCellStyle}>{historyChanged(h)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
};

// Nested table component for cells with children data
const NestedTable = ({ childrenData, childrenColumns }) => {
    if (!childrenData || !childrenColumns || childrenData.length === 0) {
        return null;
    }

    const nestedHeaderStyle = {
        backgroundColor: '#f1f5f9',
        color: '#475569',
        fontWeight: '500',
        padding: '8px 12px',
        textAlign: 'left',
        borderBottom: '1px solid #e5e7eb',
        borderRight: '1px solid #e5e7eb',
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        width: `${100 / childrenColumns.length}%`,
        minWidth: '120px'
    };

    const nestedCellStyle = {
        padding: '8px 12px',
        borderBottom: '1px solid #f3f4f6',
        borderRight: '1px solid #f3f4f6',
        color: '#374151',
        verticalAlign: 'top',
        lineHeight: '1.4',
        fontSize: '11px',
        width: `${100 / childrenColumns.length}%`,
        minWidth: '120px',
        wordWrap: 'break-word',
        overflow: 'hidden'
    };

    return (
        <table style={nestedTableStyle}>
            <thead>
                <tr>
                    {childrenColumns.map((col, index) => (
                        <th key={index} style={nestedHeaderStyle}>
                            {col.name || `Column ${index + 1}`}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {childrenData.map((childRow, rowIndex) => (
                    <tr key={rowIndex}>
                        {childRow.map((childCell, cellIndex) => {
                            let displayValue;

                            if (childCell === null || childCell === undefined) {
                                displayValue = '';
                            } else if (typeof childCell === 'boolean') {
                                displayValue = String(childCell);
                            } else if (typeof childCell === 'object') {
                                displayValue = childCell?.value ?? childCell?.length ?? '0';
                            } else if (childCell === '') {
                                displayValue = '';
                            } else {
                                displayValue = childCell;
                            }

                            return (
                                <td key={cellIndex} style={nestedCellStyle}>
                                    {displayValue}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

// virtualized table wrappers (kept module-level so identity is stable across renders)
const VirtuosoTable = React.forwardRef(({ style, ...rest }, ref) => (
    <table ref={ref} {...rest} style={{ ...tableStyle, ...style }} />
));

const VirtuosoTableRow = (props) => <tr {...props} className="crt-row" />;

const virtuosoComponents = { Table: VirtuosoTable, TableRow: VirtuosoTableRow };

// parse a usable pixel height from the maxHeight prop, falling back to the default
const parseHeight = (maxHeight) => {
    if (typeof maxHeight === 'number' && maxHeight > 0) return maxHeight;
    if (typeof maxHeight === 'string') {
        const match = maxHeight.match(/^(\d+)px$/);
        if (match) return Number(match[1]);
    }
    return 750;
};

const renderCellValue = (cell) => (
    Array.isArray(cell)
        ? cell.join(', ')
        : typeof cell === 'object'
            ? (cell?.value ?? '0')
            : typeof cell === 'boolean'
                ? (cell ? 'True' : 'False')
                : (cell ?? '0')
);

// Location history table — renders the append-only paxPositionHistory trail plus
// any handover legs (agents a booking was reallocated away from before completing,
// e.g. a landside agent handed off before the pax was walked to the gate). This is
// the trail that task-based views lose on reallocation.
const LocationHistoryTable = ({ locationHistory, handoverJourney }) => {
    const history = Array.isArray(locationHistory) ? locationHistory : [];
    const handovers = Array.isArray(handoverJourney) ? handoverJourney : [];
    const fmt = (t) => {
        if (!t) return '-';
        const d = new Date(t);
        return isNaN(d.getTime()) ? String(t) : d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    };
    if (!history.length && !handovers.length) {
        return (
            <p style={{ ...flightTitleStyle, color: '#9ca3af' }}>
                <MdLocationOn size={14} /> No location history
            </p>
        );
    }
    return (
        <div>
            <p style={flightTitleStyle}>
                <MdLocationOn size={14} color={'#2563eb'} /> Location History
            </p>
            {history.length > 0 && (
                <table style={flightTableStyle}>
                    <thead>
                        <tr>
                            {['Time', 'Activity', 'Location', 'Agent', 'Set By'].map((h, i) => (
                                <th key={i} style={flightHeaderStyle}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {history.map((e, i) => (
                            <tr key={i}>
                                <td style={flightCellStyle}>{fmt(e?.time)}</td>
                                <td style={flightCellStyle}>{e?.activity ?? '-'}</td>
                                <td style={flightCellStyle}>{e?.location ?? '-'}</td>
                                <td style={flightCellStyle}>{e?.employeeName ?? '-'}</td>
                                <td style={flightCellStyle}>{e?.setBy ?? '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {handovers.length > 0 && (
                <>
                    <p style={{ ...flightTitleStyle, marginTop: '8px' }}>Handovers (reassigned before completing)</p>
                    <table style={flightTableStyle}>
                        <thead>
                            <tr>
                                {['Agent', 'Assigned', 'Location'].map((h, i) => (
                                    <th key={i} style={flightHeaderStyle}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {handovers.map((h, i) => (
                                <tr key={i}>
                                    <td style={flightCellStyle}>{h?.employeeName ?? '-'}</td>
                                    <td style={flightCellStyle}>{fmt(h?.assignedTime ?? h?.completeTime)}</td>
                                    <td style={flightCellStyle}>{h?.location ?? h?.dropOffLocation?.name ?? '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
};

const CustomReportsTable = ({ data, columns, hideNoDataMessage, tableTitle, actionButton, renderAtLocCheckButton, maxHeight, showFlightExpand }) => {
    const [expandedFlightRow, setExpandedFlightRow] = useState(null);
    const [expandedLocationRow, setExpandedLocationRow] = useState(null);

    const tableHeight = parseHeight(maxHeight);

    // Flatten rows and their expandable nested-journey / flight-info rows into a single virtualized list
    const items = useMemo(() => {
        if (!Array.isArray(data)) return [];
        const out = [];
        data.forEach((row, rowIndex) => {
            out.push({ kind: 'row', row, rowIndex });
            const cellWithChildren = Array.isArray(row)
                ? row.find(cell => typeof cell === 'object' && cell?.childrenColumns && cell?.childrenData)
                : null;
            if (cellWithChildren) {
                out.push({ kind: 'children', cellWithChildren, rowIndex });
            }
            if (showFlightExpand && expandedFlightRow === rowIndex) {
                out.push({ kind: 'flight', flightInfo: row?.flightInfo ?? null, rowIndex });
            }
            if (showFlightExpand && expandedLocationRow === rowIndex) {
                out.push({ kind: 'location', locationHistory: row?.locationHistory ?? [], handoverJourney: row?.handoverJourney ?? [], rowIndex });
            }
        });
        return out;
    }, [data, expandedFlightRow, expandedLocationRow, showFlightExpand]);

    const handleFlightClick = (rowIndex, e) => {
        e.stopPropagation(); // Prevent row click
        setExpandedFlightRow((prev) => (prev === rowIndex ? null : rowIndex));
    }

    const handleLocationClick = (rowIndex, e) => {
        e.stopPropagation(); // Prevent row click
        setExpandedLocationRow((prev) => (prev === rowIndex ? null : rowIndex));
    }

    // fixed pixel widths by column key — prevents content from stretching columns
    const COLUMN_WIDTHS = {
        name: 180,
        assistance: 100,
        flightNumber: 100,
        preAdvised: 90,
        airport: 70,
        std: 70,
        atd: 70,
        sta: 70,
        ata: 70,
        allocated: 90,
        finished: 90,
        agent: 130,
        comments: 240,
        flightNotes: 160,
        ambiliftRequired: 110,
        numberOfJourney: 90,
        // breakdown columns
        date: 110,
        totalInboundPassengers: 90,
        totalOutboundPassengers: 90,
        total: 70,
    };
    const DEFAULT_COL_WIDTH = 100;

    const renderHeader = () => (
        <tr>
            {columns?.map((col, index) => {
                const isIndex = (col?.value || col) === 'index';
                const key = col?.key || col?.value || null;
                const label = typeof col === 'object' && col?.name !== undefined ? col.name : (isIndex ? '' : (col || `Column ${index + 1}`));
                const width = isIndex ? 24 : (COLUMN_WIDTHS[key] ?? DEFAULT_COL_WIDTH);
                return (
                    <th key={index} style={{ ...headerStyle, width, overflow: 'hidden' }}>
                        {label}
                    </th>
                );
            })}
            {showFlightExpand && <th style={{ ...headerStyle, width: 40 }}></th>}
        </tr>
    );

    const totalColSpan = (columns?.length || 0) + (showFlightExpand ? 1 : 0);

    const renderItem = (index, item) => {
        // expandable nested journey table row spanning the full width
        if (item.kind === 'children') {
            const cellWithChildren = item.cellWithChildren;
            return (
                <td colSpan={totalColSpan} style={{ padding: 35, border: 'none' }}>
                    <p style={{ ...flightTitleStyle, marginBottom: '8px' }}>
                        Task Information
                    </p>
                    <NestedTable
                        childrenData={cellWithChildren.childrenData}
                        childrenColumns={cellWithChildren.childrenColumns}
                    />
                </td>
            );
        }

        // expandable flight info row spanning the full width
        if (item.kind === 'flight') {
            return (
                <td colSpan={totalColSpan} style={{ padding: '8px 35px', border: 'none' }}>
                    <FlightInfoTable flightInfo={item.flightInfo} />
                </td>
            );
        }

        // expandable location history row spanning the full width
        if (item.kind === 'location') {
            return (
                <td colSpan={totalColSpan} style={{ padding: '8px 35px', border: 'none' }}>
                    <LocationHistoryTable locationHistory={item.locationHistory} handoverJourney={item.handoverJourney} />
                </td>
            );
        }

        const { row, rowIndex } = item;
        const isFlightExpanded = expandedFlightRow === rowIndex;
        const isLocationExpanded = expandedLocationRow === rowIndex;
        return (
            <>
                {row && row?.map && row?.map((cell, cellIndex) => {
                    const colKey = columns?.[cellIndex]?.key || columns?.[cellIndex]?.value || null;
                    const cellValue = renderCellValue(cell);
                    const showTooltip = colKey === 'comments' && typeof cellValue === 'string' && cellValue.trim().length > 0;
                    return (
                        <td
                            key={cellIndex}
                            style={cellStyle}
                            colSpan={cell?.colspan || 1}
                        >
                            {showTooltip ? (
                                <CustomTooltip text={cellValue} placement="top">
                                    <span>{cellValue}</span>
                                </CustomTooltip>
                            ) : cellValue}
                        </td>
                    );
                })}

                {/* action button column */}
                {showFlightExpand && (
                    <td style={{
                        ...cellStyle,
                        width: '110px',
                        textAlign: 'center',
                        padding: '8px',
                        ...((isFlightExpanded || isLocationExpanded) ? flightExpandedColumnStyle : {})
                    }}>
                        {actionButton ? actionButton :
                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center' }}>
                                <button
                                    className="crt-flight-btn"
                                    onClick={(e) => handleFlightClick(rowIndex, e)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        borderRadius: '4px',
                                    }}
                                    title={isFlightExpanded ? 'Hide flight info' : 'Show flight info'}
                                >
                                    <MdFlight size={18} color={isFlightExpanded ? '#2563eb' : 'grey'} style={{ cursor: 'pointer', alignSelf: 'center', alignItems: 'center' }} />
                                </button>
                                <button
                                    className="crt-flight-btn"
                                    onClick={(e) => handleLocationClick(rowIndex, e)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        borderRadius: '4px',
                                    }}
                                    title={isLocationExpanded ? 'Hide location history' : 'Show location history'}
                                >
                                    <MdLocationOn size={18} color={isLocationExpanded ? '#2563eb' : 'grey'} style={{ cursor: 'pointer', alignSelf: 'center', alignItems: 'center' }} />
                                </button>
                            </div>
                        }
                    </td>
                )}
            </>
        );
    };

    return (
        <div style={{ width: '100%' }}>
            <style>{CRT_STYLE}</style>

            {(!data || data.length === 0) && !hideNoDataMessage ? (
                <div style={emptyStateStyle}>
                    <h3 style={{ fontSize: '16px', fontWeight: '500' }}>
                        No Data Available
                    </h3>
                    <p style={{ margin: 0, fontSize: '14px' }}>
                        No report data to display at this time.
                    </p>
                </div>
            ) : (
                <TableVirtuoso
                    style={{ ...containerStyle, height: tableHeight, width: '100%', overflowX: 'auto' }}
                    data={items}
                    components={virtuosoComponents}
                    fixedHeaderContent={renderHeader}
                    itemContent={renderItem}
                    computeItemKey={(index, item) => item.kind === 'children' ? `c-${item.rowIndex}` : item.kind === 'flight' ? `f-${item.rowIndex}` : `r-${item.rowIndex}`}
                />
            )}
        </div>
    );
}

export default CustomReportsTable
